/*
 *   Module Loader
 *   Consumes an array of .js-modules and parses data-module attribute to produce an array
 *   of module names to be loaded.
 *
 *   Markup must include class="js-module" and
 *   data-module="['my-module-1', 'my-module-2'"]
 *
 */

import { camelize } from '@/ui/scripts/lib/camelize';

const PL_DEBUG = window.plDebug || false;

function load(key: string) {
  const modules: Hash = {
    // Accordion
    accordion: async () => {
      const { default: accordion } = await import(
        '@/ui/templates/02-components/accordion/mando-ui.accordion'
      );
      accordion();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Banner Notification
    bannerNotification: async () => {
      const { default: bannerNotification } = await import(
        '@/ui/templates/02-components/banner-notification/mando-ui.banner-notification'
      );
      bannerNotification();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Hero
    hero: async () => {
      const { default: hero } = await import('@/ui/templates/02-components/hero/mando-ui.hero');
      hero();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Fade In
    fadeIn: async () => {
      const { default: fadeIn } = await import('@/ui/scripts/shared/mando-ui.fade-in');
      fadeIn();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Featured Update Card
    featuredUpdateCard: async () => {
      const { default: featuredUpdateCard } = await import(
        '@/ui/templates/02-components/subcomponents/featured-update-card/mando-ui.featured-update-card'
      );
      featuredUpdateCard();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Filter
    filter: async () => {
      const { default: filter } = await import('@/ui/scripts/shared/mando-ui.filter');
      filter();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // iFrame
    iframe: async () => {
      const { default: iframe } = await import(
        '@/ui/templates/02-components/iframe/mando-ui.iframe'
      );
      iframe();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Image Gallery
    imageGallery: async () => {
      const { default: imageGallery } = await import(
        '@/ui/templates/02-components/image-gallery/mando-ui.image-gallery'
      );
      imageGallery();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Nav
    nav: async () => {
      const { default: nav } = await import(
        '@/ui/templates/02-components/subcomponents/header/_nav/mando-ui.nav'
      );
      nav();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Pagination
    pagination: async () => {
      const { default: pagination } = await import(
        '@/ui/templates/02-components/pagination/mando-ui.pagination'
      );
      pagination();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Project Hero Banner Map
    projectHeroBannerMap: async () => {
      const { default: projectHeroBannerMap } = await import(
        '@/app/views/project-hero-banner-map/project-hero-banner-map'
      );
      projectHeroBannerMap();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Project Progress Tracker
    projectProgress: async () => {
      const { default: projectProgress } = await import(
        '@/ui/templates/02-components/project-statusbar/mando-ui.project-statusbar'
      );
      projectProgress();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Planned Project Area
    plannedProjectArea: async () => {
      const { default: plannedProjectArea } = await import(
        '@/app/views/planned-project-area/planned-project-area'
      );
      plannedProjectArea();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Projects Map
    projectsMap: async () => {
      const { default: projectsMap } = await import('@/app/views/projects-map/projects-map');
      projectsMap();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Project Search CTA
    projectSearchCta: async () => {
      const { default: projectSearchCta } = await import(
        '@/app/views/project-search-cta/project-search-cta'
      );
      projectSearchCta();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Recite Me
    reciteMe: async () => {
      const { default: reciteMe } = await import(
        '@/ui/templates/02-components/recite-me/mando-ui.recite-me'
      );
      reciteMe();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // RTE Table
    rteTable: async () => {
      const { default: rteTable } = await import(
        '@/ui/templates/02-components/subcomponents/_rte-table/mando-ui.rte-table'
      );
      rteTable();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Searchbar
    searchbar: async () => {
      const { default: searchbar } = await import(
        '@/ui/templates/02-components/subcomponents/header/_searchbar/mando-ui.searchbar'
      );
      searchbar();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Storytelling Gallery
    storytellingGallery: async () => {
      const { default: storytellingGallery } = await import(
        '@/ui/templates/02-components/storytelling-gallery/mando-ui.storytelling-gallery'
      );
      storytellingGallery();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // Tabs
    tabs: async () => {
      const { default: tabs } = await import(
        '@/ui/templates/02-components/subcomponents/tabs/tabs/mando-ui.tabs'
      );
      tabs();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // triageTabs
    triage: async () => {
      const { default: triage } = await import(
        '@/ui/templates/02-components/triage/mando-ui.triage'
      );
      triage();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
    // videoCard
    videoCard: async () => {
      const { default: triage } = await import(
        '@/ui/templates/02-components/video-card/mando-ui.video-card'
      );
      triage();
      if (PL_DEBUG) console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
    },
  };

  try {
    return modules[key]();
  } catch (e) {
    console.error(`Module ${key} not found.`);
  }
}

function init(modules: HTMLElement[]): void {
  if (!modules) return;
  const moduleList: string[] = [];
  modules.forEach((element) => {
    try {
      const moduleNames: string | undefined = element.dataset.module;
      if (moduleNames) {
        const moduleArray: string[] = JSON.parse(moduleNames.replace(/'/g, '"'));
        moduleArray.forEach((item: string) => {
          const module: string = camelize(item);
          if (moduleList.indexOf(module) === -1) moduleList.push(module);
        });
      }
    } catch (e) {
      console.error('JS name format incorrect in data attribute:');
      console.log(moduleList);
    }
  });

  moduleList.forEach((module) => load(module));
}

export default init;
